import { isSessionActivityAuditable } from '../../domain/constants/auditableSessionActivityStatuses';
import { AUDIT_TASK_ERROR_CODES } from '../../domain/constants/auditTaskErrorCodes';
import { createAuditTask } from '../../domain/entities/AuditTask';
import { AIUnavailableError } from '../../domain/errors/AIUnavailableError';

const CONCURRENCY = 5;

/**
 * Creates an audit run that processes session activities concurrently.
 * @param {Function} auditSessionActivity - Calls the audit API for a single session activity
 * @returns {{ prepare: Function, run: Function, cancel: Function }}
 */
// eslint-disable-next-line import/prefer-default-export
export function createAuditRun(auditSessionActivity) {
  let abortController = null;
  let isRunning = false;

  /**
   * Prepare the audit tasks based on the provided session activities.
   * @param {Object[]} sessionActivities - The session activities to audit
   * @returns {AuditTask[]}
   */
  function prepare(sessionActivities) {
    return sessionActivities.map((sessionActivity) => {
      const task = createAuditTask(sessionActivity);

      if (!isSessionActivityAuditable(sessionActivity)) {
        task.markFailed({
          code: AUDIT_TASK_ERROR_CODES.INVALID_SESSION_ACTIVITY_STATUS,
        });
      }

      return task;
    });
  }

  /**
   * Process a single task: mark processing, call the API, store result or error.
   * @param {AuditTask} task - The audit task to process
   * @param {AuditSettings} settings - The audit settings to use
   * @param {AbortSignal} signal
   * @returns {Promise<void>}
   */
  async function processTask(task, settings, signal) {
    if (!isSessionActivityAuditable(task.sessionActivity)) {
      task.markFailed({
        code: AUDIT_TASK_ERROR_CODES.INVALID_SESSION_ACTIVITY_STATUS,
      });
      return;
    }

    task.markProcessing();
    try {
      const { verdict } = await auditSessionActivity({
        clinicId: task.sessionActivity.clinic,
        sessionActivityId: task.sessionActivity.id,
        focusAreas: settings.focusAreas,
        strictness: settings.strictness,
      }, { signal });
      task.markCompleted(verdict);
    } catch (err) {
      if (signal.aborted) {
        task.markCancelled();
        return;
      }

      task.markFailed({
        code: err instanceof AIUnavailableError ? AUDIT_TASK_ERROR_CODES.AI_UNAVAILABLE : null,
      });
    }
  }

  /**
   * Worker loop: repeatedly pull the next pending task and process it.
   * Exits when no pending tasks remain.
   * @param {AuditTask[]} tasks - The full list of tasks to pull work from
   * @param {AuditSettings} settings - Audit settings to pass to each task
   * @param {AbortSignal} signal
   * @param {function} [onTaskComplete] - Optional callback invoked after each
   * task completes, with the completed task as argument.
   * @returns {Promise<void>}
   */
  async function worker(tasks, settings, signal, onTaskComplete = null) {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (signal.aborted) {
        return;
      }
      const next = tasks.find((t) => t.isPending());
      if (!next) {
        return;
      }
      // eslint-disable-next-line no-await-in-loop
      await processTask(next, settings, signal);
      if (typeof onTaskComplete === 'function') {
        onTaskComplete(next);
      }
    }
  } // worker()

  /**
   * Run the audit process: start multiple workers to process tasks concurrently.
   * Prevents multiple simultaneous runs and ensures all tasks are processed before finishing.
   * @param {AuditTask[]} tasks - The reactive task list to operate on; workers mutate these objects
   * @param {AuditSettings} settings - Audit settings to pass to each task
   * @param {function} [onTaskComplete] - Optional callback invoked after each task completes
   * @returns {Promise<void>}
   */
  async function run(tasks, settings, onTaskComplete = null) {
    if (isRunning) {
      throw new Error('Audit run already in progress');
    }
    isRunning = true;
    abortController = new AbortController();
    const { signal } = abortController;
    const workers = Array.from(
      { length: Math.min(CONCURRENCY, tasks.length) },
      () => worker(tasks, settings, signal, onTaskComplete),
    );
    try {
      await Promise.all(workers);
    } finally {
      isRunning = false;
    }

    if (signal.aborted) {
      tasks.filter((t) => t.isPending()).forEach((t) => t.markCancelled());
    }
  } // run()

  /**
   * Cancel the ongoing audit process by aborting the workers. This will cause in-flight
   * API calls to fail and mark remaining tasks as canceled.
   * @returns {void}
   */
  function cancel() {
    abortController?.abort();
  } // cancel()

  return { prepare, run, cancel };
}
